import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FrameRecords, SideDisplayConfig } from "../types/api";
import { allColumns, applyPins, cellText } from "../lib/columns";
import {
  holdsAmount, ledgerColumns, policyMatcher, sortPositions, trackValues,
  type PolicyTarget, type SortSpec,
} from "../lib/ledgerView";
import { rawKeyOf, sideOf, sideSpans } from "../lib/columnGroups";
import { BAND_LABEL_ATTR, useBandLabels } from "../lib/bandLabels";
import { useGridSelection } from "./useGridSelection";
import { useColumnWidths } from "./useColumnWidths";
import { useColumnFilters } from "../lib/columnFilters";
import { useColumnPins, type PinSide } from "../lib/columnPins";
import { useFrozenOffsets } from "../lib/frozenOffsets";
import { makeVisibleMatcher } from "../lib/textSearch";
import {
  findInMatched, groupHitsByMatch, type MatchedHit, type SearchTerm,
} from "../lib/multiSearch";
import {
  counterpartSigs, isPriorPeriodMatch, isSettledBeforeMatch, matchGroupIdOf, sigAmount,
  sigLabel, sigSide,
} from "../lib/manualMatchLabels";
import type { PriorSettlement } from "../lib/priorPeriodMatch";
import type { AutoClearing } from "../lib/clearedRelease";
import { matchGroupCellClass } from "../lib/matchGroups";
import MatchDetailTable from "./MatchDetailTable";
import { fmtAed } from "./KpiCard";
import EmptyState from "./EmptyState";
import InfoTip from "./InfoTip";
import FullscreenTable from "./FullscreenTable";
import { HeadControls, TrackChips } from "./GridControls";
import { SearchPanel, useMultiSearch } from "./SearchPanel";

interface Props {
  frame: FrameRecords;         // must include a "_sig" column (hidden) for selection
  sigColumn: string;           // name of the hidden signature column
  selectedSig: string | null;
  onSelect: (sig: string | null) => void;
  pageSize?: number;
  config?: SideDisplayConfig;  // curated columns + friendly names (declutter)
  // Pin scope — shares the statement grids' scope for this side. Omitted → no pinning.
  pinSide?: PinSide;
  insurerName?: string;
  // MULTI-SELECT (opt-in): when set, the leading control becomes a checkbox and every
  // sig in this set renders as selected. `selectedSig` still drives single-select for
  // every other caller, so passing neither of these keeps the original radio grid.
  selectedSigs?: Set<string>;
  // Bulk select/deselect of every row currently surviving the filters. Opt-in alongside
  // selectedSigs: pasting 24 references is only half the job finance asked for — they
  // then have to TICK those 24 rows, and doing it by hand is the same one-by-one grind
  // the paste was meant to end.
  onSelectMany?: (sigs: string[], selected: boolean) => void;
  // The FULL frame for this side (this grid renders the unmatched SLICE). Supplying it
  // switches on "found in matched": a pasted value absent from the unmatched rows is
  // looked up among the already-matched ones, so the user learns the matcher paired it
  // wrongly instead of being told it does not exist. Omitted → that panel never renders.
  fullFrame?: FrameRecords;
  // Release an auto-match so both its legs return to the open buckets and can be paired
  // by hand. Given together with fullFrame.
  onRelease?: (sig: string) => void | Promise<void>;
  // Act on every match the panel lists: `groups` are the auto-matches to release (one
  // inner array per match, see `bulk`), `unmatchSigs` one leg of each manual match to
  // unmatch (see onUnmatch), `prior` the prior-period pairs to undo (see onUndoPrior).
  onReleaseAll?: (groups: string[][], unmatchSigs: string[],
                  prior: PriorSettlement[]) => void | Promise<void>;
  releaseBusy?: boolean;
  // Every leg of the MANUAL match holding `sig`, or undefined when the app matched it. A
  // manual match is applied after releases, so Release cannot reach it — its line offers
  // Unmatch (onUnmatch) instead, in the same place.
  manualGroupOf?: (sig: string) => string[] | undefined;
  onUnmatch?: (sig: string) => void | Promise<void>;
  // The prior-period item a row settled, from its match_group_id (priorSettlementOf). A
  // prior-period pair is applied after releases too, so its line undoes it through the
  // item instead (onUndoPrior): Reject for an automatic clear, Unmatch for a hand pair.
  priorSettlementOf?: (gid: string) => PriorSettlement | null;
  onUndoPrior?: (s: PriorSettlement) => void | Promise<void>;
  // An OPEN row the app paired as Cleared in Subsequent Month (lib/clearedRelease): listed
  // here too, as only a release undoes it (onRelease).
  clearedOf?: (sig: string) => AutoClearing | undefined;
  // Paired frames only: composite keys (meta::/cri::/ins::) render the spanning Match |
  // CRI | insurer banner above the headers, as DataTable `grouped` does.
  grouped?: boolean;
  // Names a column holding a per-row block id; consecutive rows of one block are drawn as
  // one unit, and `blockHeader` (when it returns a node) opens each block with a
  // full-width section row. Only for frames already in block order.
  groupKeyCol?: string;
  blockHeader?: (block: string) => React.ReactNode;
  // Row sigs surviving the search + column filters while either is active, null when
  // neither is. Lets a parent scope a bulk action to exactly what the user can see.
  onFilteredChange?: (sigs: string[] | null) => void;
  // The OPPOSITE book's full frame, plus its display config. A match has two sides, and
  // Release frees both — so the panel must show the counterpart it is about to free, not
  // just the leg the user's search landed on. Without it finance was asked to approve
  // un-matching a pair while seeing half of it. Omitted → the counterpart line is skipped.
  otherFrame?: FrameRecords;
  otherConfig?: SideDisplayConfig;
  // What the search/filter reset keys on. Frame identity is the WRONG discriminator here:
  // a re-finalize (a manual match, a RELEASE, a note) rebuilds the frame for the same
  // ledger, and resetting there wipes the query the user is working under. Releasing one
  // pair from the matched-hits panel did exactly that — the panel is derived from the
  // pasted text, so clearing the box made every remaining entry vanish at once, which
  // reads as "it released all of them". Pass a key that changes only on a genuinely
  // different ledger; omitted, it falls back to frame identity (the historic behaviour).
  resetKey?: string;
  // Title shown in the full-screen header bar.
  title?: string;
  // Manual Match density (finance 2026-09-26): the heading, search box and full-screen
  // toggle on ONE toolbar row. (One-line cells, once part of this, are every grid's since
  // 2026-09-28: measured on Tokio, wrapped free text made rows 92-111px tall.)
  compact?: boolean;
  // Leads the toolbar row in compact mode (the grid's own name and counts).
  heading?: React.ReactNode;
  // Scroll-box height outside its own full screen (the default is 420).
  maxHeight?: number;
  // THE MANUAL MATCH LEDGERS (finance 2026-09-27). The matching fields lead, columns that
  // repeat another column's data go last, and the two status columns are hidden when constant
  // (lib/ledgerView.ts); newest rows first; Premium/Commission/Claims chips; book-coloured
  // ticks. Needs pinSide for the colour. Sort, amounts with separators, shift-click range
  // ticking and hover-only header controls are every grid's (2026-09-28).
  ledgerView?: boolean;
  // "Find this amount": the parent asks this grid to show only rows whose amountColumn
  // holds `amountFind` on either sign. Only on request (null otherwise); the chip it puts
  // above the grid clears it via onClearAmountFind.
  amountColumn?: string;
  amountFind?: number | null;
  onClearAmountFind?: () => void;
  // "Find this policy": likewise, rows carrying this policy (ledgerView.policyMatcher).
  // Both finds apply together when both are set.
  policyFind?: PolicyTarget | null;
  onClearPolicyFind?: () => void;
}

// Excel-style filter grid with single- or multi-select. EVERY column carries a filter in
// its header cell (icon opens a searchable checklist popover of distinct values; ● marks
// active); filtering is a per-column .isin AND, plus a global text search. Rows are
// selectable via a leading radio (single) or checkbox (multi, when selectedSigs is
// given). Filter state + distinct-value caching come from the shared useColumnFilters
// hook; the popover (shared HeaderFilter) portals to document.body.
// Width of the leading select column. Pinned columns freeze to the RIGHT of it, so the
// select control stays reachable while the grid scrolls sideways.
const RADIO_W = 34;

export default function ColumnFilterGrid(props: Props) {
  return (
    <FullscreenTable label={props.title || "Rows"} inlineToggle={props.compact}>
      {(scrollMax, toggle) => (
        <ColumnFilterGridBody {...props} scrollMax={scrollMax ?? props.maxHeight}
          fullscreenToggle={toggle} />
      )}
    </FullscreenTable>
  );
}

function ColumnFilterGridBody({ frame, sigColumn, selectedSig, onSelect, pageSize = 200, config,
                                pinSide, insurerName = "", selectedSigs, onSelectMany,
                                fullFrame, onRelease, onReleaseAll, releaseBusy, resetKey,
                                otherFrame, otherConfig, manualGroupOf, onUnmatch,
                                priorSettlementOf, onUndoPrior, clearedOf, grouped = false, groupKeyCol, blockHeader, onFilteredChange,
                                compact = false, heading, scrollMax, fullscreenToggle,
                                ledgerView = false, amountColumn, amountFind = null,
                                onClearAmountFind, policyFind = null, onClearPolicyFind,
                              }: Props & { scrollMax?: number; fullscreenToggle?: React.ReactNode }) {
  const [openCol, setOpenCol] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  // The ledger this grid is showing, NOT the frame object — see `resetKey` on Props.
  const identity = resetKey ?? frame;
  const cf = useColumnFilters(frame, resetKey);
  const pins = useColumnPins(pinSide ?? "cri", insurerName);
  const pinnable = pinSide !== undefined;
  const book = ledgerView;
  // Rows drawn as match blocks are never sorted: a sort would split a match's legs apart.
  const sortable = !groupKeyCol;
  // Ledger row order and track chip. Both books open newest first, on their own doc_date;
  // every other grid keeps its caller's order until the user sorts.
  const defaultSort: SortSpec | null = ledgerView && frame.columns.includes("doc_date")
    ? { key: "doc_date", dir: "desc" } : null;
  const [sort, setSort] = useState<SortSpec | null>(defaultSort);
  const [track, setTrack] = useState<string | null>(null);

  // The page still has to come back in range when the ROWS change under a surviving
  // search (a release adds a row to the unmatched slice, and a shrinking result set can
  // otherwise leave the view parked past the last page showing nothing).
  useEffect(() => { setPage(0); }, [frame]);

  // Which matched-hit line is expanded to full per-transaction detail. Release un-matches
  // real rows, and the one-line label is capped at a few fields — so the reference that
  // separates two similar transactions may not be in it at all. Clicking a line opens
  // every column of every row that release would free. One at a time: these are wide
  // tables, and the question is always about ONE pair.
  const [detailFor, setDetailFor] = useState<string | null>(null);

  const sigIdx = frame.columns.indexOf(sigColumn);
  const gkIdx = groupKeyCol ? frame.columns.indexOf(groupKeyCol) : -1;
  // allColumns (not visibleColumns): curated display columns first, then EVERY other
  // populated source column. The manual-match picker is where a user identifies a row
  // with no obvious key, so the curated list was the wrong set here — it hid the CRI
  // reference_no / endorsement_number and the insurer's raw SOA headers, i.e. exactly
  // the fields needed to find the counterpart and filter on it. Matches DataTable.
  const vis = useMemo(() => {
    let cols = allColumns(frame, config).filter((c) => c.key !== sigColumn);
    // Repeats are judged over the whole book when the parent supplies it (see
    // repeatedColumns), so the order holds steady as rows get matched away.
    if (book) cols = ledgerColumns(cols, fullFrame ?? frame, frame.rows);
    return pinnable ? applyPins(cols, pins.pinned) : cols;
  }, [frame, config, sigColumn, pinnable, pins.pinned, book, fullFrame]);

  const frozen = useFrozenOffsets(pinnable ? pins.pinned : [], vis, RADIO_W);
  const lastFrozen = useMemo(() => {
    let last: string | undefined;
    for (const c of vis) if (frozen.offsetFor(c.key) !== undefined) last = c.key;
    return last;
  }, [vis, frozen]);

  // Side banner over each run of same-side columns, from the FINAL column order (pins can
  // interleave sides) — the DataTable `grouped` device, measured the same way.
  const spans = useMemo(() => (grouped ? sideSpans(vis, insurerName) : []),
    [grouped, vis, insurerName]);
  const seamKeys = useMemo(() => new Set(spans.slice(1).map((sp) => sp.firstKey)), [spans]);
  const bandLabels = useBandLabels(grouped, spans);
  const bannerRef = useRef<HTMLTableRowElement | null>(null);
  const [groupH, setGroupH] = useState(26);
  useEffect(() => {
    if (!grouped) return;
    const measure = () => {
      const h = bannerRef.current?.getBoundingClientRect().height;
      if (h) setGroupH((prev) => (Math.abs(prev - h) > 0.5 ? h : prev));
    };
    const raf = requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", measure); };
  }, [grouped, spans]);

  // The search panel's state (SearchPanel.tsx). A lone typed value is checked for being
  // missing too when the found-in-matched panel is on: a searched reference sitting on a
  // wrong auto-match must be releasable as well.
  const search = useMultiSearch(frame, vis, { missingForOneValue: !!(fullFrame && onRelease) });
  const { text, setText, searchCol, setSearchCol, exact, setExact, terms, multi, searchCols,
          missing } = search;

  useEffect(() => {
    setText(""); setOpenCol(null); setPage(0); setSearchCol(""); setExact(false);
    setSort(defaultSort); setTrack(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity]);

  const trackIdx = book ? frame.columns.indexOf("track") : -1;
  const tracks = useMemo(() => (book ? trackValues(frame) : []), [book, frame]);
  const amountIdx = amountFind !== null && amountColumn ? frame.columns.indexOf(amountColumn) : -1;
  const policyMatch = useMemo(
    () => (policyFind ? policyMatcher(frame, policyFind) : null), [frame, policyFind]);

  // Filter to row POSITIONS as well as rows, so each surviving row keeps a link back to
  // its frame.indices entry — the stable id the cell selection's stale-guard keys on.
  // Built in ONE pass (not a second reference-matching walk), mirroring DataTable.
  const { narrowed, narrowedIds, trackCounts } = useMemo(() => {
    const rows: FrameRecords["rows"] = [];
    const ids: (string | number)[] = [];
    // Per track, over everything else that narrows the book, so each chip's count is
    // what clicking it would show.
    const counts = new Map<string, number>();
    const keep = cf.rowPredicate(frame.columns);
    const test = search.rowTest;
    for (let p = 0; p < frame.rows.length; p++) {
      const r = frame.rows[p];
      if (keep && !keep(r)) continue;
      if (test && !test(p, r)) continue;
      if (amountIdx >= 0 && !holdsAmount(r[amountIdx], amountFind as number)) continue;
      if (policyMatch && !policyMatch(p)) continue;
      if (trackIdx >= 0) {
        const tv = String(r[trackIdx] ?? "");
        counts.set(tv, (counts.get(tv) ?? 0) + 1);
        if (track !== null && tv !== track) continue;
      }
      rows.push(r);
      ids.push(frame.indices[p] ?? p);
    }
    return { narrowed: rows, narrowedIds: ids, trackCounts: counts };
  }, [frame, cf, search.rowTest, amountIdx, amountFind, policyMatch, trackIdx, track]);

  // Sorted AFTER filtering, rows and ids together, so the selection's positions and ids
  // stay paired.
  const { filtered, filteredIds } = useMemo(() => {
    const order = sortable ? sortPositions(frame, narrowed, sort) : null;
    if (!order) return { filtered: narrowed, filteredIds: narrowedIds };
    return { filtered: order.map((p) => narrowed[p]), filteredIds: order.map((p) => narrowedIds[p]) };
  }, [narrowed, narrowedIds, sort, sortable, frame]);

  // Every ALREADY-MATCHED row of the full frame holding a searched value, whether or not
  // that value is also in this (unmatched) grid.
  //
  // This is the case finance hit: the matcher paired a reference with the wrong
  // counterpart, and a matched row can never be manually re-paired (the engine only
  // pairs UNMATCHED rows). Scanning only the not-found values hid the matched rows of any
  // value that ALSO had an unmatched row (finance 2026-09-28).
  const matchedHits = useMemo(() => {
    if (!fullFrame || !onRelease || !terms.length) return [];
    const statusIdx = fullFrame.columns.indexOf("match_status");
    const sigIdx = fullFrame.columns.indexOf("_sig");
    if (statusIdx < 0 || sigIdx < 0) return [];
    // HONOUR THE USER'S SCOPE. `missing` is computed over searchCols, so scanning the
    // whole frame here would answer a DIFFERENT question than the one asked: narrowed to
    // "Reference No", a ref absent from that column but appearing incidentally inside
    // some unrelated matched row's narration would be offered for release — pointing
    // finance at the wrong pair on the one action that un-matches real rows.
    // Column KEYS are shared between the full frame and its unmatched slice, so the
    // narrowing maps across by key; only the index is re-resolved.
    const cols = (searchCol ? searchCols : vis)
      .map((c) => ({ key: c.key, label: c.label, index: fullFrame.columns.indexOf(c.key) }))
      .filter((c) => c.index >= 0 && !c.key.startsWith("_"));
    if (!cols.length) return [];
    // Now on every keystroke, not only on a miss, so each needle is pre-tested against the
    // cached per-frame haystack the grid search uses (~2ms a scan on Dubai-sized frames,
    // against a full cell walk). A haystack cell carries every form cellForms tests, so it
    // never rejects a real hit; findInMatched confirms each survivor cell by cell.
    const pre = new Map(terms.map((t) => [t.needle, makeVisibleMatcher(fullFrame, cols, t.needle)]));
    const admit = clearedOf
      ? (p: number) => clearedOf(String(fullFrame.rows[p][sigIdx] ?? ""))?.status ?? null
      : undefined;
    return findInMatched(fullFrame.rows, cols, terms, exact, statusIdx, sigIdx,
      (p, needle) => pre.get(needle)!(p), admit);
  }, [fullFrame, onRelease, terms, exact, searchCol, searchCols, vis, clearedOf]);

  // How many searched values have no unmatched row and were found ONLY among the matched
  // ones: the rows the user most likely came to fix, so the panel opens for them and folds
  // when every value is also in the grid.
  const nOnlyMatched = useMemo(() => {
    const miss = new Set(missing.map((t) => t.needle));
    const hit = new Set<string>();
    for (const h of matchedHits) for (const t of h.terms) if (miss.has(t.needle)) hit.add(t.needle);
    return hit.size;
  }, [matchedHits, missing]);

  // WHO made a match decides how it is undone, so each hit carries its kind. The app's own
  // pairs are RELEASED. A manual pair is applied after releases, so a release can never
  // reach it — it is UNMATCHED instead, on the same line (finance 2026-09-25: 22 Union
  // rows paired by hand "could not be released"). A prior-period pair is undone through
  // its item, on the same line too (finance 2026-10-14, AL BUHAIRA S1216721).
  const hitKind = useCallback((sig: string): "auto" | "manual" | "prior" | "cleared" => {
    if (manualGroupOf?.(sig)) return "manual";
    if (fullFrame && isPriorPeriodMatch(fullFrame, sig)) return "prior";
    return clearedOf?.(sig) ? "cleared" : "auto";
  }, [manualGroupOf, fullFrame, clearedOf]);
  const priorOf = useCallback((sig: string): PriorSettlement | null =>
    fullFrame && priorSettlementOf ? priorSettlementOf(matchGroupIdOf(fullFrame, sig)) : null,
  [fullFrame, priorSettlementOf]);
  // A settled-before pair has no item to undo it through: its row is released.
  const settledBefore = useCallback((sig: string): boolean =>
    !!fullFrame && isSettledBeforeMatch(fullFrame, sig), [fullFrame]);

  // ONE LINE PER HAND-MADE MATCH. Its line lists every leg, so a 22-leg group hit by 22
  // pasted values rendered 22 identical lines — one decision buried 22 times. The line
  // keeps every value that led to it; an auto-match keeps one line per value as before.
  const panelItems = useMemo(() => {
    const out: { hit: MatchedHit; terms: SearchTerm[] }[] = [];
    const byMatch = new Map<string, { hit: MatchedHit; terms: SearchTerm[] }>();
    for (const h of matchedHits) {
      const legs = manualGroupOf?.(h.sig);
      if (!legs) { out.push({ hit: h, terms: h.terms }); continue; }
      const key = [...legs].sort().join("\u0001");
      const at = byMatch.get(key);
      if (at) {
        for (const t of h.terms) if (!at.terms.includes(t)) at.terms.push(t);
        continue;
      }
      const item = { hit: h, terms: [...h.terms] };
      byMatch.set(key, item);
      out.push(item);
    }
    return out;
  }, [matchedHits, manualGroupOf]);

  // The panel's open state for THIS query: the user's own toggle, else the default taken
  // the first time the query has hits. Fixed per query so a release, which can move the
  // searched value into the unmatched grid, never folds the panel under the user.
  const [matchedOpenQ, setMatchedOpenQ] = useState<boolean | null>(null);
  // A broad search (a customer name) can hit thousands of matched rows; every one is found
  // and counted, but lines render a batch at a time.
  const PANEL_BATCH = 50;
  const [panelShown, setPanelShown] = useState(PANEL_BATCH);
  useEffect(() => { setMatchedOpenQ(null); setPanelShown(PANEL_BATCH); }, [text, searchCol, exact]);
  useEffect(() => {
    if (matchedOpenQ === null && matchedHits.length) setMatchedOpenQ(nOnlyMatched > 0);
  }, [matchedOpenQ, matchedHits.length, nOnlyMatched]);
  const matchedOpen = matchedOpenQ ?? nOnlyMatched > 0;

  // "Release all": ONE entry per auto-match, holding only the searched legs' own
  // signatures — exactly what clicking each Release would store, minus repeats. Hits are
  // grouped through their counterparts, so two pasted values landing on the same match
  // (both legs, or two legs of an aggregate) release it once; storing it twice would mean
  // a later Restore of one entry left the match still released by the other. Counterpart
  // signatures are used to GROUP, never stored: a signature is not unique within a book,
  // so storing one the user never searched for could seed an unrelated match's release.
  // Manual matches contribute ONE leg each (one per panel line); the parent unmatches the
  // whole match from it. A prior-period pair contributes its ITEM once, however many of
  // its rows (an aggregation group's riders) the search hit.
  const bulk = useMemo(() => {
    if (!fullFrame) {
      return { release: [] as string[][], unmatch: [] as string[], prior: [] as PriorSettlement[] };
    }
    const auto = matchedHits.filter((h) => hitKind(h.sig) === "auto").map((h) => h.sig);
    const prior = new Map<string, PriorSettlement>();
    const before: string[][] = [];
    for (const h of matchedHits) {
      // An app-made Cleared pairing is released by the leg the search hit, like SB:.
      if (hitKind(h.sig) === "cleared") { before.push([h.sig]); continue; }
      if (hitKind(h.sig) !== "prior") continue;
      if (settledBefore(h.sig)) { before.push([h.sig]); continue; }
      const s = priorOf(h.sig);
      if (s?.undo) prior.set(`${s.book}|${s.sig}`, s);
    }
    return {
      release: [...groupHitsByMatch(auto, (s) => counterpartSigs(fullFrame, otherFrame, s)),
                ...before],
      unmatch: panelItems.filter((i) => hitKind(i.hit.sig) === "manual").map((i) => i.hit.sig),
      prior: [...prior.values()],
    };
  }, [fullFrame, otherFrame, matchedHits, hitKind, priorOf, settledBefore, panelItems]);
  const nBulk = bulk.release.length + bulk.unmatch.length + bulk.prior.length;
  const bulkLabel = !bulk.prior.length
    ? (!bulk.unmatch.length ? `Release all ${nBulk} pairs`
      : !bulk.release.length ? `Unmatch all ${nBulk} matches`
      : `Release / unmatch all ${nBulk}`)
    : nBulk === bulk.prior.length ? `Undo all ${nBulk} prior-period matches`
    : `Undo all ${nBulk} matches`;

  // Reset to page 1 when the filter/search changes.
  useEffect(() => { setPage(0); }, [cf.colFilters, text, searchCol, exact, track, amountFind, policyFind, sort]);

  // Signatures of every row surviving the filters — the set the bulk control acts on.
  // Deliberately the WHOLE filtered set, not the visible page: the user searched for 24
  // references and means all 24, and a page-scoped "select all" that quietly took 200 of
  // them would be a silent partial match on the one action where completeness is the
  // entire point.
  const filteredSigs = useMemo(() => {
    if (!onSelectMany || sigIdx < 0) return [];
    return filtered.map((r) => String(r[sigIdx]));
  }, [onSelectMany, filtered, sigIdx]);

  // Report what survives the search + filters, so a parent's bulk action can be scoped
  // to exactly what is on screen. null while nothing narrows the rows.
  const filterActive = cf.nActive > 0 || text.trim() !== "" || track !== null || amountIdx >= 0;
  useEffect(() => {
    if (!onFilteredChange) return;
    onFilteredChange(filterActive && sigIdx >= 0
      ? filtered.map((r) => String(r[sigIdx])) : null);
  }, [onFilteredChange, filterActive, filtered, sigIdx]);

  const nFilteredSelected = useMemo(() => {
    if (!selectedSigs || !filteredSigs.length) return 0;
    let n = 0;
    for (const s of filteredSigs) if (selectedSigs.has(s)) n++;
    return n;
  }, [selectedSigs, filteredSigs]);

  // Excel-style cell selection + live totals, the SAME hook the statement grids use.
  // Finance reconciles by footing a block of amounts (that is how the Union mixed-sign
  // basket was read off Excel in the first place), so the manual-match picker — where
  // they decide what to match — needs it just as much as the read-only tables.
  // Row coordinates are positions in `filtered`, not the page slice.
  const colIdx = useMemo(() => vis.map((c) => c.index), [vis]);
  const colLabels = useMemo(() => vis.map((c) => c.label), [vis]);
  // Ctrl/Cmd+C is scoped to the focused grid by the hook — load-bearing here, because
  // the manual-match tab renders two of these side by side.
  const sel = useGridSelection(filtered, colIdx, colLabels, filteredIds);
  const cw = useColumnWidths(useMemo(() => ["__select", ...vis.map((c) => c.key)], [vis]), frame);
  // Autofit reads every filtered row, not just this page.
  const colTexts = (c: (typeof vis)[number]) => () =>
    filtered.map((r) => cellText(r[c.index], grouped ? rawKeyOf(c.key) : c.key));

  const pageRows = filtered.slice(page * pageSize, (page + 1) * pageSize);
  const nPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  // Shift-click ticks every row between the last ticked row and this one, all set to the
  // state the clicked row is taking (Explorer / Gmail). Positions are in `filtered`, so the
  // anchor is dropped whenever the rows on screen change under it.
  const anchorRef = useRef<number | null>(null);
  useEffect(() => { anchorRef.current = null; }, [filtered]);
  const tickRow = (pos: number, sig: string, wasChecked: boolean, shift: boolean) => {
    const from = anchorRef.current;
    anchorRef.current = pos;
    if (shift && from !== null && from !== pos && onSelectMany && sigIdx >= 0) {
      const [a, b] = from < pos ? [from, pos] : [pos, from];
      onSelectMany(filtered.slice(a, b + 1).map((r) => String(r[sigIdx])), !wasChecked);
      return;
    }
    onSelect(sig);
  };

  return (
    <div className={book ? `cfg-ledger cfg-ledger--${pinSide ?? "cri"}` : undefined}>
      <SearchPanel search={search} matchingRows={filtered.length} heading={heading} compact={compact}
        tools={<>
          {cf.nActive > 0 && (
            <button className="btn btn-sm"
              onClick={cf.clearAll}>
              Clear {cf.nActive} filter{cf.nActive === 1 ? "" : "s"}
            </button>
          )}
          {fullscreenToggle && <span className="msearch-fs">{fullscreenToggle}</span>}
        </>}>

        {/* Bulk select — the second half of what finance asked for. Acts on every
            FILTERED row (not the page), so a search that surfaced 24 rows ticks all 24
            in one click. */}
        {onSelectMany && selectedSigs && filteredSigs.length > 0 && filterActive && (
          <div className="msearch-actions">
            <button
              className={nFilteredSelected < filteredSigs.length ? "btn btn-primary" : "btn"}
              onClick={() => onSelectMany(filteredSigs, nFilteredSelected < filteredSigs.length)}
            >
              {nFilteredSelected < filteredSigs.length
                ? `Select all ${filteredSigs.length.toLocaleString()} matching row${filteredSigs.length === 1 ? "" : "s"}`
                : `Deselect these ${filteredSigs.length.toLocaleString()} row${filteredSigs.length === 1 ? "" : "s"}`}
            </button>
            {nFilteredSelected > 0 && (
              <span className="muted">
                {nFilteredSelected.toLocaleString()} of {filteredSigs.length.toLocaleString()} selected
              </span>
            )}
          </div>
        )}

        {/* FOUND IN MATCHED. A pasted value that is not in the unmatched rows but sits on
            an already-matched one, which can never be manually re-paired. Each line offers
            the undo that fits who made the match (hitKind) — Release or Unmatch, both
            freeing every leg back into the open buckets so they can be paired correctly.
            Tinted as information, not risk. */}
        {matchedHits.length > 0 && (
          <div className="msearch-matched">
            {/* COLLAPSIBLE. This panel sits ABOVE the grid, so a long list of matched
                hits pushes the ledger table off screen — and the table is what the user
                is comparing against. The summary line stays visible (the count is the
                part that must never be hidden), while the rows fold away. Open by
                default: on the run that surfaces them, they are what was searched for. */}
            <button type="button"
              className={"msearch-matched-head msearch-matched-toggle"
                + (matchedOpen ? " is-open" : "")}
              aria-expanded={matchedOpen}
              onClick={() => setMatchedOpenQ(!matchedOpen)}>
              <span className="msearch-caret" aria-hidden="true">▾</span>
              <span>
                {(() => {
                  const n = `${matchedHits.length.toLocaleString()} matched row${matchedHits.length === 1 ? "" : "s"}`;
                  if (!multi) {
                    return nOnlyMatched ? `Not in the unmatched rows — found on ${n}`
                      : `This value is also on ${n}`;
                  }
                  return `${n} also contain searched values`
                    + (nOnlyMatched ? ` · ${nOnlyMatched} of the not-found value${nOnlyMatched === 1 ? " is" : "s are"} among them` : "");
                })()}
              </span>
              <span className="msearch-matched-hint">
                {matchedOpen ? "Hide" : "Show"}
              </span>
            </button>
            {/* Outside the button: a tooltip inside a <button> is not keyboard-reachable
                and its click would toggle the panel. */}
            <div className="msearch-matched-tip">
              <InfoTip text="These rows hold a searched value but are already matched — and a matched row cannot be matched again. If a match is wrong, undo it here: Release for a match the app made, Unmatch for one made by hand (it is also removed from the remembered matches). Both sides return to the unmatched lists here, where you can pair them correctly. Your closing balances and the Difference do not change. A row settled against a prior-period item is undone through that item: Reject when the app settled it (the item is held open, and the decision is remembered), Unmatch when it was paired by hand (it is removed from the remembered prior-period matches). The item is then open again, so the Adjusted Closing Balance moves by its amount. A row the app treated as settled before this period is released like any app match, and so is a row the app paired with a later transaction (Cleared in Subsequent Month): it is open at period end again." />
            </div>
            {matchedOpen && (
            <div className="msearch-matched-note">
              Each line shows the WHOLE match — every row listed on it. Click a line to see
              the full detail of those transactions. Release, Unmatch or Reject acts on that
              one match only: those rows return to the lists above and other matches here
              are untouched. The Difference does not change.
            </div>
            )}
            {matchedOpen && onReleaseAll && nBulk > 1 && (
            <div className="msearch-matched-actions">
              <button className="btn btn-sm" disabled={releaseBusy}
                title={"Undo every match listed here in one update: the app's matches are released (each saved as its own entry, restorable on its own) and hand-made ones are unmatched and removed from the remembered matches."
                  + (bulk.prior.length
                    ? " Prior-period matches are undone through their items: the app's are rejected (the item held open), hand-made ones forgotten. Those items are open again, so the Adjusted Closing Balance moves."
                    : " Your totals and the Difference do not change.")}
                onClick={() => {
                  if (window.confirm(`${bulkLabel}? Every row on those lines returns to the unmatched lists.`
                      + (panelItems.length > panelShown
                        ? ` This includes the ${(panelItems.length - panelShown).toLocaleString()} line${panelItems.length - panelShown === 1 ? "" : "s"} not shown yet.` : "")
                      + (bulk.unmatch.length ? " Hand-made matches are also removed from the remembered matches." : "")
                      + (bulk.prior.length
                        ? ` ${bulk.prior.length} prior-period item${bulk.prior.length === 1 ? " is" : "s are"} open again: the app's settlements are rejected and hand-made ones forgotten, so the Adjusted Closing Balance moves.`
                        : " Totals and the Difference do not change."))) {
                    onReleaseAll(bulk.release, bulk.unmatch, bulk.prior);
                  }
                }}>
                {releaseBusy ? "Working…" : bulkLabel}
              </button>
            </div>
            )}
            {matchedOpen && (
            <div className="msearch-matched-list">
              {panelItems.slice(0, panelShown).map(({ hit: h, terms: hitTerms }) => {
                const kind = hitKind(h.sig);
                // BOTH LEGS. Undoing a match frees the whole match, so the panel has to
                // show the whole match — the leg the search hit AND every other leg. An
                // auto-match's other legs are its pointer counterparts; a manual match's
                // are its stored group, which for a within-ledger offset sits in THIS book.
                const others = kind === "manual"
                  ? (manualGroupOf?.(h.sig) ?? []).filter((s) => s !== h.sig)
                  : kind === "auto" ? counterpartSigs(fullFrame!, otherFrame, h.sig)
                  : kind === "cleared" ? clearedOf?.(h.sig)?.others ?? [] : [];
                const own = sigSide(h.sig);
                const sameBook = (s: string) => !sigSide(s) || sigSide(s) === own;
                const leg = (sig: string) => {
                  const here = sameBook(sig) || !otherFrame;
                  const frame = here ? fullFrame! : otherFrame;
                  return (
                    <span className="msearch-leg" key={sig}>
                      {sigSide(sig) && <span className="msearch-side">{sigSide(sig)}</span>}
                      {sigLabel(frame, sig, here ? config : otherConfig)}
                      {/* The AMOUNT, which sigLabel omits by design. It is what finance
                          checks first to judge whether the app tied the right two
                          transactions — a pair is only credible if the sides offset. */}
                      {sigAmount(frame, sig) !== null && (
                        <span className="msearch-amt">{fmtAed(sigAmount(frame, sig))}</span>
                      )}
                    </span>
                  );
                };
                // One line per signature (findInMatched), and a manual match's line is
                // keyed by the first of its legs the search hit.
                const rowKey = h.sig;
                const open = detailFor === rowKey;
                const before = kind === "prior" && settledBefore(h.sig);
                const prior = kind === "prior" && !before ? priorOf(h.sig) : null;
                const plural = others.length
                  ? `all ${others.length + 1} rows shown on this line`
                  : "both its legs (CRI and insurer)";
                return (
                <div key={rowKey} className="msearch-matched-item">
                  <div className="msearch-matched-row">
                    <span className="msearch-term"
                      title={hitTerms.length > 1 ? hitTerms.map((t) => t.raw).join("\n") : undefined}>
                      {hitTerms[0].raw}
                      {hitTerms.length > 1 && ` +${hitTerms.length - 1} more searched`}
                    </span>
                    {/* The legs are the DISCLOSURE control: clicking the summary opens
                        every column of every row this line would free. A one-line
                        label cannot carry enough to identify a transaction with
                        confidence, and this action un-matches real rows. */}
                    <button type="button"
                      className={"grow msearch-legs msearch-legs--btn" + (open ? " is-open" : "")}
                      aria-expanded={open}
                      title={open ? "Hide the full transaction detail"
                                  : "Show the full detail of every row in this match"}
                      onClick={() => setDetailFor(open ? null : rowKey)}>
                      <span className="msearch-caret" aria-hidden="true">▾</span>
                      <span className="msearch-legs-stack">
                        {leg(h.sig)}
                        {others.map((s) => leg(s))}
                        {/* Say so explicitly when the other side cannot be resolved,
                            rather than rendering a one-sided pair that reads as
                            complete. */}
                        {kind === "auto" && otherFrame && others.length === 0 && (
                          <span className="msearch-leg msearch-leg--none">
                            counterpart not in this view
                          </span>
                        )}
                        {kind === "prior" && !prior && (
                          <span className="msearch-leg msearch-leg--none">
                            {before ? "settled before this period (its counterpart predates the window)"
                              : "settled against a prior-period item"}
                          </span>
                        )}
                        {/* The item is the other leg: name it like one, so finance can
                            judge the pair before undoing it. */}
                        {prior && (
                          <span className="msearch-leg">
                            <span className="msearch-side">
                              Prior period · {prior.book === "cri" ? "CRI" : "Insurer"} list
                            </span>
                            {prior.ref || "item"}
                            {prior.amount !== null && (
                              <span className="msearch-amt">{fmtAed(prior.amount)}</span>
                            )}
                          </span>
                        )}
                      </span>
                    </button>
                    <span className="msearch-chip msearch-chip--info">
                      {kind === "manual" ? `${h.status} · by hand`
                        : kind === "prior" ? `${h.status} · prior period` : h.status}
                    </span>
                    {kind === "auto" && (
                      <button className="btn msearch-release" disabled={releaseBusy}
                        title={`Release this one pair — ${plural} return to the unmatched lists. Your totals and the Difference do not change.`}
                        onClick={() => onRelease?.(h.sig)}>
                        {releaseBusy ? "Releasing…" : "Release"}
                      </button>
                    )}
                    {kind === "manual" && onUnmatch && (
                      <button className="btn msearch-release" disabled={releaseBusy}
                        title={`This match was made by hand. Unmatch it — ${plural} return to the unmatched lists, and it is removed from the remembered matches so it stops re-applying. Your totals and the Difference do not change.`}
                        onClick={() => onUnmatch(h.sig)}>
                        {releaseBusy ? "Working…" : "Unmatch"}
                      </button>
                    )}
                    {prior?.undo === "reject" && onUndoPrior && (
                      <button className="btn msearch-release" disabled={releaseBusy}
                        title="The app settled this prior-period item against this row. Reject it: the row returns to the unmatched lists and the item is held open, a decision remembered for future uploads (Restore it under Prior-period matches below). The Adjusted Closing Balance moves by the item; the Difference does not."
                        onClick={() => onUndoPrior(prior)}>
                        {releaseBusy ? "Working…" : "Reject"}
                      </button>
                    )}
                    {prior?.undo === "forget" && onUndoPrior && (
                      <button className="btn msearch-release" disabled={releaseBusy}
                        title="This prior-period item was paired with this row by hand. Unmatch it: the row returns to the unmatched lists, the item is open again, and the pair is removed from the remembered prior-period matches so it stops re-applying."
                        onClick={() => onUndoPrior(prior)}>
                        {releaseBusy ? "Working…" : "Unmatch"}
                      </button>
                    )}
                    {kind === "cleared" && onRelease && (
                      <button className="btn msearch-release" disabled={releaseBusy}
                        title="The app paired this row with a transaction dated after the period (Cleared in Subsequent Month). Release that pairing: the item is open at period end again, back on Group 1 / Group 2, and the later row returns to its own list. Restore it from the Released matches list."
                        onClick={() => onRelease(h.sig)}>
                        {releaseBusy ? "Releasing…" : "Release"}
                      </button>
                    )}
                    {before && onRelease && (
                      <button className="btn msearch-release" disabled={releaseBusy}
                        title="The app treated this row as settled by a transaction dated before the period. Release it: the row returns to the unmatched lists and its earlier counterpart leaves the Matched tab. Restore it from the Released matches list. The Difference does not change."
                        onClick={() => onRelease(h.sig)}>
                        {releaseBusy ? "Releasing…" : "Release"}
                      </button>
                    )}
                    {kind === "prior" && !before && !(prior?.undo && onUndoPrior) && (
                      <span className="muted msearch-prior-hint">
                        {prior ? "Forget it under Prior-period matches below"
                          : "Undo on the Prior-Period Unreconciled tab"}
                      </span>
                    )}
                  </div>
                  {open && (() => {
                    // This grid is ONE book. Which is which decides the bands, so every
                    // leg is placed by the engine's self-identifying signature, falling
                    // back to the grid's own pin scope only when a signature has no prefix.
                    const thisIsIns = own === "Insurer" || (own === "" && pinSide === "insurer");
                    const legs = [h.sig, ...others];
                    const onIns = (s: string) => (sameBook(s) ? thisIsIns : !thisIsIns);
                    return (
                      <div className="msearch-detail">
                        <MatchDetailTable
                          criFrame={thisIsIns ? otherFrame : fullFrame}
                          criSigs={legs.filter((s) => !onIns(s))}
                          criConfig={thisIsIns ? otherConfig : config}
                          insFrame={thisIsIns ? fullFrame : otherFrame}
                          insSigs={legs.filter(onIns)}
                          insConfig={thisIsIns ? config : otherConfig}
                          insurerName={insurerName} />
                      </div>
                    );
                  })()}
                </div>
                );
              })}
              {panelItems.length > panelShown && (
                <button type="button" className="btn btn-sm"
                  onClick={() => setPanelShown((n) => n + PANEL_BATCH)}>
                  Show {Math.min(PANEL_BATCH, panelItems.length - panelShown)} more
                  {" "}({(panelItems.length - panelShown).toLocaleString()} not shown)
                </button>
              )}
            </div>
            )}
          </div>
        )}
      </SearchPanel>

      <TrackChips tracks={tracks} counts={trackCounts} track={track} onTrack={setTrack} />
      {amountIdx >= 0 && (
        <div className="ledger-find" role="status">
          <span>
            Showing rows of <strong>{fmtAed(amountFind)}</strong> on either sign
            · {filtered.length.toLocaleString()} row{filtered.length === 1 ? "" : "s"}
          </span>
          <button type="button" className="btn btn-sm" onClick={onClearAmountFind}>
            {policyFind ? "Clear amount" : "Show all rows"}
          </button>
        </div>
      )}
      {policyFind && (
        <div className="ledger-find" role="status">
          <span>
            Showing rows of policy <strong>{policyFind.full}</strong>
            {amountIdx >= 0 ? " and that amount" : ""}
            {" "}· {filtered.length.toLocaleString()} row{filtered.length === 1 ? "" : "s"}
          </span>
          <button type="button" className="btn btn-sm" onClick={onClearPolicyFind}>
            {amountIdx >= 0 ? "Clear policy" : "Show all rows"}
          </button>
        </div>
      )}

      <div {...sel.gridProps}>
      <div
        ref={(el) => {
          frozen.scrollRef.current = el; sel.scrollHostRef.current = el; bandLabels.register(el);
        }}
        className="grid-scroll" style={{ maxHeight: scrollMax ?? 420 }}>
        <table className={"data-table" + (pinnable && pins.pinned.length ? " data-table--separate" : "")
            + (grouped ? " data-table--grouped" : "") + cw.className}
          style={grouped ? ({ "--group-h": `${groupH}px`, ...cw.style } as React.CSSProperties) : cw.style}>
          {cw.colgroup}
          <thead>
            {grouped && (
              <tr ref={bannerRef}>
                <th className={"side-band side-band--meta" + (lastFrozen ? " side-band--pinned" : "")}
                  style={{ width: RADIO_W, left: lastFrozen ? 0 : undefined }} />
                {spans.map((sp) => {
                  // A run whose every column is frozen freezes its banner too, at the
                  // run's own offset, so the label stays over the columns it names.
                  const off = sp.keys.every((k) => frozen.offsetFor(k) !== undefined)
                    ? frozen.offsetFor(sp.firstKey) : undefined;
                  const left = off === undefined ? undefined : off + RADIO_W;
                  return (
                    <th key={sp.firstKey} colSpan={sp.span} style={{ left }}
                      className={`side-band side-band--${sp.side}`
                        + (left !== undefined ? " side-band--pinned" : "")}>
                      <span className="band-label" {...{ [BAND_LABEL_ATTR]: "" }}
                        data-band-pinned={left !== undefined ? "1" : undefined}>
                        {sp.label}
                      </span>
                    </th>
                  );
                })}
              </tr>
            )}
            <tr>
              {/* Radio stays frozen at the far left whenever anything else is, so the
                  row can still be selected after scrolling sideways. */}
              <th className={[lastFrozen ? "pinned-col" : "", grouped ? "col-head" : ""].join(" ")}
                style={{ width: RADIO_W, left: lastFrozen ? 0 : undefined }}></th>
              {vis.map((c, ci) => {
                const off = frozen.offsetFor(c.key);
                const left = off === undefined ? undefined : off + RADIO_W;
                const cls = [
                  left !== undefined ? "pinned-col" : "",
                  left !== undefined && c.key === lastFrozen ? "pinned-edge" : "",
                  sel.isColSelected(ci) ? "col-selected" : "",
                  grouped ? "col-head" : "",
                  seamKeys.has(c.key) ? "side-start-ins" : "",
                ].filter(Boolean).join(" ");
                return (
                  <th key={c.key} data-col-key={c.key} className={`nowrap ${cls}`} style={{ left }}>
                    <span className="th-inner">
                      {/* Only the LABEL selects the column — the filter and pin controls
                          sit outside it, so opening a filter never foots a column. */}
                      <span className="clickable"
                        onClick={(e) => { sel.onHeaderClick(ci, e); sel.focusGrid(); }}>
                        {c.label}
                      </span>
                      <HeadControls frame={frame} col={c}
                        filter={{
                          active: (cf.colFilters[c.key]?.length ?? 0) > 0,
                          open: openCol === c.key,
                          options: () => cf.distinctFor(c),
                          selected: cf.colFilters[c.key] ?? [],
                          onToggleOpen: () => setOpenCol(openCol === c.key ? null : c.key),
                          onChange: (v) => cf.setColFilter(c.key, v),
                        }}
                        sort={sortable ? { spec: sort, onSort: setSort } : undefined}
                        pin={pinnable ? { pinned: pins.isPinned(c.key), onToggle: () => pins.toggle(c.key) } : undefined} />
                    </span>
                    {cw.grip(c.key, colTexts(c))}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r, ri) => {
              const rowPos = page * pageSize + ri;  // position in `filtered`, the selection's coordinate space
              const sig = sigIdx >= 0 ? String(r[sigIdx]) : String(ri);
              const multi = selectedSigs !== undefined;
              const checked = multi ? selectedSigs.has(sig) : selectedSig === sig;
              // Block banding from the RENDERED neighbours (DataTable's rule): a filter or
              // page boundary that splits a block restarts it, and re-opens its section
              // row, rather than bracketing rows the reader cannot see.
              const gk = gkIdx >= 0 ? String(r[gkIdx] ?? "") : "";
              const prevGk = gkIdx >= 0 && ri > 0 ? String(pageRows[ri - 1][gkIdx] ?? "") : "";
              const nextGk = gkIdx >= 0 && ri + 1 < pageRows.length
                ? String(pageRows[ri + 1][gkIdx] ?? "") : "";
              const head = gk !== "" && gk !== prevGk && blockHeader ? blockHeader(gk) : null;
              const inBlock = gk !== "" && (head !== null || gk === prevGk || gk === nextGk);
              const rowCls = [
                checked ? "row-checked" : "",
                inBlock ? "mgrp-row" + (gk === prevGk ? "" : " mgrp-row-start") : "",
              ].filter(Boolean).join(" ") || undefined;
              return (
                <Fragment key={rowPos}>
                {head !== null && (
                  <tr className="mblock-head">
                    <td colSpan={vis.length + 1}>
                      <div className="mblock-head-inner">{head}</div>
                    </td>
                  </tr>
                )}
                <tr className={rowCls}>
                  <td className={`cell-center ${lastFrozen ? "pinned-col" : ""}`}
                    style={{ left: lastFrozen ? 0 : undefined }}>
                    {/* Multi-select toggles the sig (the parent owns the set); single-select
                        keeps its click-again-to-clear behaviour. */}
                    {multi ? (
                      // onClick, not onChange: only the click event carries shiftKey.
                      <input type="checkbox" checked={checked} readOnly
                        onClick={(e) => tickRow(rowPos, sig, checked, e.shiftKey)} />
                    ) : (
                      <input type={multi ? "checkbox" : "radio"} checked={checked}
                        onChange={() => onSelect(!multi && checked ? null : sig)} />
                    )}
                  </td>
                  {vis.map((c, ci) => {
                    const v = r[c.index];
                    const key = grouped ? rawKeyOf(c.key) : c.key;
                    const text = cellText(v, key);
                    const off = frozen.offsetFor(c.key);
                    const left = off === undefined ? undefined : off + RADIO_W;
                    const cls = [
                      "cell-clip",
                      typeof v === "number" ? "num" : "",
                      left !== undefined ? "pinned-col" : "",
                      left !== undefined && c.key === lastFrozen ? "pinned-edge" : "",
                      sel.isSelected(rowPos, ci) ? "cell-selected" : "",
                      grouped && sideOf(c.key) === "ins" ? "side-ins" : "",
                      seamKeys.has(c.key) ? "side-start-ins" : "",
                      matchGroupCellClass(c.key, v),
                    ].filter(Boolean).join(" ");
                    return (
                      <td key={c.key} className={cls} style={{ left }}
                        title={text}
                        data-row={rowPos} data-col={ci}
                        onMouseDown={(e) => { sel.onCellMouseDown(rowPos, ci, e); sel.focusGrid(); }}
                        onMouseEnter={() => sel.onCellMouseEnter(rowPos, ci)}>
                        {text}
                      </td>
                    );
                  })}
                </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Same gap as DataTable: filtering to nothing left a bare header. Here it
          matters more — a pasted list that matches no row is exactly when the user
          needs to be told, rather than shown an empty grid. */}
      {filtered.length === 0 && (
        <EmptyState
          title="No rows match the current filters"
          body={text.trim()
            ? "Nothing in this grid matches what you searched or pasted."
            : cf.nActive > 0 ? "Every row is filtered out by the column filters above."
            : "No row in this book matches the track or amount chosen above."}
          action={{
            label: text.trim() ? "Clear search"
              : cf.nActive > 0 ? `Clear ${cf.nActive} filter${cf.nActive === 1 ? "" : "s"}`
              : "Show all rows",
            onClick: () => {
              if (text.trim()) setText("");
              else if (cf.nActive > 0) cf.clearAll();
              else { setTrack(null); onClearAmountFind?.(); }
            },
          }}
        />
      )}
      {sel.statusBar}
      </div>
      {filtered.length > 0 && (
        <div className="grid-pager">
          <button className="btn" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>Prev</button>
          <span className="muted">Page {page + 1} of {nPages} · {filtered.length.toLocaleString()} rows</span>
          <button className="btn" onClick={() => setPage((p) => Math.min(nPages - 1, p + 1))} disabled={page >= nPages - 1}>Next</button>
        </div>
      )}
    </div>
  );
}
